// ============================================================
// COREBANK — CRM ENGINE
// ============================================================
// Relationship manager home screen: contact management, task
// management, customer 360 view, RM portfolio, pipelines.
//
// Depends on: KYCEngine, LendingEngine, MonitoringEngine,
//             ComplianceEngine, PaymentOrchestrator, EventBus
// ============================================================

let _seq = 0;
function genId(pfx = '') {
  _seq++;
  return `${pfx}${Date.now().toString(36)}${_seq.toString(36).padStart(4, '0')}${Math.random().toString(36).slice(2, 6)}`;
}

// ---- Types ----

const ContactType = { MEETING: 'MEETING', CALL: 'CALL', EMAIL: 'EMAIL', NOTE: 'NOTE', VISIT: 'VISIT' };
const TaskPriority = { HIGH: 'HIGH', MEDIUM: 'MEDIUM', LOW: 'LOW' };
const TaskStatus = { OPEN: 'OPEN', COMPLETED: 'COMPLETED' };
const ComplaintChannel = { PHONE: 'PHONE', EMAIL: 'EMAIL', LETTER: 'LETTER', IN_PERSON: 'IN_PERSON', ONLINE: 'ONLINE' };
const ComplaintStatus = { RECEIVED: 'RECEIVED', ACKNOWLEDGED: 'ACKNOWLEDGED', INVESTIGATING: 'INVESTIGATING', RESOLVED: 'RESOLVED', ESCALATED_TO_FOS: 'ESCALATED_TO_FOS' };

const COMPLAINT_TRANSITIONS = {
  [ComplaintStatus.RECEIVED]: [ComplaintStatus.ACKNOWLEDGED],
  [ComplaintStatus.ACKNOWLEDGED]: [ComplaintStatus.INVESTIGATING],
  [ComplaintStatus.INVESTIGATING]: [ComplaintStatus.RESOLVED, ComplaintStatus.ESCALATED_TO_FOS],
  [ComplaintStatus.RESOLVED]: [ComplaintStatus.INVESTIGATING],       // reopen
  [ComplaintStatus.ESCALATED_TO_FOS]: [],
};

const EIGHT_WEEKS_MS = 8 * 7 * 24 * 60 * 60 * 1000;
const SIX_WEEKS_MS = 6 * 7 * 24 * 60 * 60 * 1000;

// ---- Engine ----

class CRMEngine {
  constructor(kycEngine, lendingEngine, monitoringEngine, complianceEngine, orchestrator, eventBus) {
    this.kyc = kycEngine;
    this.lending = lendingEngine;
    this.monitoring = monitoringEngine;
    this.compliance = complianceEngine;
    this.orchestrator = orchestrator;
    this.eventBus = eventBus || null;
    this.contacts = new Map();     // contactId → contact
    this.tasks = new Map();        // taskId → task
    this.complaints = new Map();   // complaintId → complaint

    if (this.eventBus) {
      this._subscribeToEvents();
    }
  }

  // ============================================================
  // CONTACT MANAGEMENT
  // ============================================================

  addContactRecord(customerId, { type, subject, notes, rmId, date }) {
    if (!customerId) throw new Error('customerId is required');
    if (!type || !Object.values(ContactType).includes(type)) {
      throw new Error(`Invalid contact type: ${type}`);
    }
    if (!subject) throw new Error('subject is required');

    const contact = {
      id: genId('contact_'),
      customerId,
      type,
      subject,
      notes: notes || '',
      rmId: rmId || null,
      date: date || new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };
    this.contacts.set(contact.id, contact);

    if (this.eventBus) {
      this.eventBus.publish({
        type: 'ContactRecorded',
        entity_id: contact.id,
        data: { contactId: contact.id, customerId, contactType: type, rmId: contact.rmId },
      });
    }

    return contact;
  }

  getContactHistory(customerId) {
    return Array.from(this.contacts.values())
      .filter(c => c.customerId === customerId)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  // ============================================================
  // TASK MANAGEMENT
  // ============================================================

  createTask({ customerId, type, title, description, assignedTo, dueDate, priority, sourceEventId }) {
    if (!title) throw new Error('title is required');
    if (priority && !Object.values(TaskPriority).includes(priority)) {
      throw new Error(`Invalid priority: ${priority}`);
    }

    const task = {
      id: genId('task_'),
      customerId: customerId || null,
      type: type || 'GENERAL',
      title,
      description: description || '',
      assignedTo: assignedTo || null,
      dueDate: dueDate || null,
      priority: priority || TaskPriority.MEDIUM,
      status: TaskStatus.OPEN,
      completedBy: null,
      completedAt: null,
      completedNotes: null,
      sourceEventId: sourceEventId || null,
      createdAt: new Date().toISOString(),
    };
    this.tasks.set(task.id, task);

    if (this.eventBus) {
      this.eventBus.publish({
        type: 'TaskCreated',
        entity_id: task.id,
        data: { taskId: task.id, customerId: task.customerId, title, assignedTo: task.assignedTo, priority: task.priority },
      });
    }

    return task;
  }

  completeTask(taskId, completedBy, notes) {
    const task = this.tasks.get(taskId);
    if (!task) throw new Error(`Task ${taskId} not found`);
    if (task.status === TaskStatus.COMPLETED) throw new Error('Task already completed');

    task.status = TaskStatus.COMPLETED;
    task.completedBy = completedBy || null;
    task.completedAt = new Date().toISOString();
    task.completedNotes = notes || null;

    if (this.eventBus) {
      this.eventBus.publish({
        type: 'TaskCompleted',
        entity_id: task.id,
        data: { taskId: task.id, customerId: task.customerId, completedBy: task.completedBy },
      });
    }

    return task;
  }

  reassignTask(taskId, newAssignee) {
    const task = this.tasks.get(taskId);
    if (!task) throw new Error(`Task ${taskId} not found`);
    if (!newAssignee) throw new Error('newAssignee is required');
    task.assignedTo = newAssignee;
    return task;
  }

  getTasksByRM(rmId) {
    return Array.from(this.tasks.values())
      .filter(t => t.assignedTo === rmId && t.status === TaskStatus.OPEN);
  }

  getTasksByCustomer(customerId) {
    return Array.from(this.tasks.values())
      .filter(t => t.customerId === customerId);
  }

  getOverdueTasks() {
    const now = new Date().toISOString();
    return Array.from(this.tasks.values())
      .filter(t => t.status === TaskStatus.OPEN && t.dueDate && t.dueDate < now);
  }

  // ============================================================
  // EVENT SUBSCRIPTIONS — AUTO-GENERATED TASKS
  // ============================================================

  _subscribeToEvents() {
    this.eventBus.subscribe('KYCStatusChanged', (event) => {
      try {
        const data = event.data || {};
        if (data.to === 'REVIEW_DUE') {
          this.createTask({
            customerId: data.customerId,
            type: 'KYC_REVIEW',
            title: 'KYC periodic review due',
            description: `KYC status changed to REVIEW_DUE for customer ${data.customerId}`,
            priority: TaskPriority.HIGH,
            sourceEventId: event.event_id,
          });
        }
      } catch (e) {
        console.error('CRM: error handling KYCStatusChanged:', e.message);
      }
    });

    this.eventBus.subscribe('AlertCreated', (event) => {
      try {
        const data = event.data || {};
        if (data.severity === 'HIGH' || data.severity === 'CRITICAL') {
          this.createTask({
            customerId: data.customerId,
            type: 'ALERT_REVIEW',
            title: 'Review high-severity alert',
            description: `Alert ${data.alertId || ''} — ${data.type || 'unknown'} (${data.severity})`,
            priority: TaskPriority.HIGH,
            sourceEventId: event.event_id,
          });
        }
      } catch (e) {
        console.error('CRM: error handling AlertCreated:', e.message);
      }
    });

    this.eventBus.subscribe('SARCreated', (event) => {
      try {
        const data = event.data || {};
        this.createTask({
          customerId: data.customerId,
          type: 'SAR_NARRATIVE',
          title: 'Complete SAR narrative',
          description: `SAR ${data.sarId} created — reason: ${data.reason || 'N/A'}`,
          priority: TaskPriority.HIGH,
          sourceEventId: event.event_id,
        });
      } catch (e) {
        console.error('CRM: error handling SARCreated:', e.message);
      }
    });
  }

  // ============================================================
  // COMPLAINTS MANAGEMENT (FCA DISP)
  // ============================================================

  createComplaint(customerId, { subject, description, channel, receivedBy, receivedDate }) {
    if (!customerId) throw new Error('customerId is required');
    if (!subject) throw new Error('subject is required');
    if (!channel || !Object.values(ComplaintChannel).includes(channel)) {
      throw new Error(`Invalid channel: ${channel}`);
    }

    const now = new Date().toISOString();
    const complaint = {
      id: genId('complaint_'),
      customerId,
      subject,
      description: description || '',
      channel,
      status: ComplaintStatus.RECEIVED,
      receivedBy: receivedBy || null,
      receivedDate: receivedDate || now,
      acknowledgedBy: null,
      acknowledgedAt: null,
      investigation: null,
      rootCause: null,
      proposedResolution: null,
      resolution: null,
      resolvedBy: null,
      resolvedAt: null,
      redressAmount: null,
      upheld: null,
      escalatedToFOS: false,
      fosReason: null,
      createdAt: now,
    };
    this.complaints.set(complaint.id, complaint);

    if (this.eventBus) {
      this.eventBus.publish({
        type: 'ComplaintCreated',
        entity_id: complaint.id,
        data: { complaintId: complaint.id, customerId, subject, channel },
      });
    }

    // Auto-task: acknowledge within 24 hours
    const dueDateAck = new Date(new Date(complaint.receivedDate).getTime() + 24 * 60 * 60 * 1000).toISOString();
    this.createTask({
      customerId,
      type: 'COMPLAINT_ACK',
      title: 'Acknowledge complaint within 24 hours',
      description: `Complaint ${complaint.id}: ${subject}`,
      priority: TaskPriority.HIGH,
      dueDate: dueDateAck,
      sourceEventId: complaint.id,
    });

    return complaint;
  }

  acknowledgeComplaint(complaintId, acknowledgedBy) {
    const c = this.complaints.get(complaintId);
    if (!c) throw new Error(`Complaint ${complaintId} not found`);
    this._transitionComplaint(c, ComplaintStatus.ACKNOWLEDGED);
    c.acknowledgedBy = acknowledgedBy || null;
    c.acknowledgedAt = new Date().toISOString();

    if (this.eventBus) {
      this.eventBus.publish({
        type: 'ComplaintAcknowledged',
        entity_id: c.id,
        data: { complaintId: c.id, customerId: c.customerId, acknowledgedBy: c.acknowledgedBy },
      });
    }

    return c;
  }

  updateComplaint(complaintId, { investigation, rootCause, proposedResolution }) {
    const c = this.complaints.get(complaintId);
    if (!c) throw new Error(`Complaint ${complaintId} not found`);
    if (c.status === ComplaintStatus.RESOLVED || c.status === ComplaintStatus.ESCALATED_TO_FOS) {
      throw new Error(`Cannot update complaint in ${c.status} status`);
    }

    if (investigation !== undefined) c.investigation = investigation;
    if (rootCause !== undefined) c.rootCause = rootCause;
    if (proposedResolution !== undefined) c.proposedResolution = proposedResolution;

    // Auto-transition to INVESTIGATING if currently ACKNOWLEDGED
    if (c.status === ComplaintStatus.ACKNOWLEDGED) {
      this._transitionComplaint(c, ComplaintStatus.INVESTIGATING);
    }

    return c;
  }

  resolveComplaint(complaintId, { resolution, resolvedBy, redressAmount, upheld }) {
    const c = this.complaints.get(complaintId);
    if (!c) throw new Error(`Complaint ${complaintId} not found`);
    if (!resolution) throw new Error('resolution is required');
    this._transitionComplaint(c, ComplaintStatus.RESOLVED);

    c.resolution = resolution;
    c.resolvedBy = resolvedBy || null;
    c.resolvedAt = new Date().toISOString();
    c.redressAmount = redressAmount != null ? redressAmount : null;
    c.upheld = upheld != null ? upheld : null;

    if (this.eventBus) {
      this.eventBus.publish({
        type: 'ComplaintResolved',
        entity_id: c.id,
        data: { complaintId: c.id, customerId: c.customerId, upheld: c.upheld, redressAmount: c.redressAmount },
      });
    }

    return c;
  }

  escalateToFOS(complaintId, { reason }) {
    const c = this.complaints.get(complaintId);
    if (!c) throw new Error(`Complaint ${complaintId} not found`);
    if (!reason) throw new Error('reason is required');
    this._transitionComplaint(c, ComplaintStatus.ESCALATED_TO_FOS);

    c.escalatedToFOS = true;
    c.fosReason = reason;

    if (this.eventBus) {
      this.eventBus.publish({
        type: 'ComplaintEscalatedToFOS',
        entity_id: c.id,
        data: { complaintId: c.id, customerId: c.customerId, reason },
      });
    }

    return c;
  }

  reopenComplaint(complaintId, { reason }) {
    const c = this.complaints.get(complaintId);
    if (!c) throw new Error(`Complaint ${complaintId} not found`);
    if (c.status !== ComplaintStatus.RESOLVED) {
      throw new Error('Can only reopen RESOLVED complaints');
    }
    this._transitionComplaint(c, ComplaintStatus.INVESTIGATING);
    c.resolvedAt = null;
    c.resolvedBy = null;
    c.resolution = null;
    c.redressAmount = null;
    c.upheld = null;

    return c;
  }

  getComplaintsByCustomer(customerId) {
    return Array.from(this.complaints.values())
      .filter(c => c.customerId === customerId)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  getOpenComplaints() {
    return Array.from(this.complaints.values())
      .filter(c => c.status !== ComplaintStatus.RESOLVED && c.status !== ComplaintStatus.ESCALATED_TO_FOS);
  }

  getOverdueComplaints() {
    const now = Date.now();
    return Array.from(this.complaints.values())
      .filter(c => {
        if (c.status === ComplaintStatus.RESOLVED || c.status === ComplaintStatus.ESCALATED_TO_FOS) return false;
        const received = new Date(c.receivedDate).getTime();
        return (now - received) >= EIGHT_WEEKS_MS;
      });
  }

  getComplaintsDashboard() {
    const all = Array.from(this.complaints.values());
    const byStatus = {};
    for (const s of Object.values(ComplaintStatus)) byStatus[s] = 0;
    for (const c of all) byStatus[c.status] = (byStatus[c.status] || 0) + 1;

    const resolved = all.filter(c => c.status === ComplaintStatus.RESOLVED);
    let avgResolutionMs = 0;
    if (resolved.length > 0) {
      const totalMs = resolved.reduce((sum, c) => {
        if (c.resolvedAt && c.receivedDate) {
          return sum + (new Date(c.resolvedAt).getTime() - new Date(c.receivedDate).getTime());
        }
        return sum;
      }, 0);
      avgResolutionMs = totalMs / resolved.length;
    }

    const upheldCount = resolved.filter(c => c.upheld === true).length;
    const redressTotal = resolved.reduce((sum, c) => sum + (c.redressAmount || 0), 0);

    return {
      total: all.length,
      byStatus,
      open: this.getOpenComplaints().length,
      overdue: this.getOverdueComplaints().length,
      avgResolutionDays: Math.round(avgResolutionMs / 86400000 * 10) / 10,
      upheldRate: resolved.length > 0 ? Math.round((upheldCount / resolved.length) * 100) : 0,
      redressTotal,
    };
  }

  // Check for complaints approaching the 8-week deadline
  checkComplaintDeadlines() {
    const now = Date.now();
    const tasks = [];
    for (const c of this.complaints.values()) {
      if (c.status === ComplaintStatus.RESOLVED || c.status === ComplaintStatus.ESCALATED_TO_FOS) continue;
      const elapsed = now - new Date(c.receivedDate).getTime();
      if (elapsed >= SIX_WEEKS_MS && elapsed < EIGHT_WEEKS_MS) {
        // Check if we already created this task
        const existing = Array.from(this.tasks.values()).find(
          t => t.sourceEventId === `deadline_${c.id}` && t.status === TaskStatus.OPEN
        );
        if (!existing) {
          const task = this.createTask({
            customerId: c.customerId,
            type: 'COMPLAINT_DEADLINE',
            title: 'Complaint approaching 8-week deadline',
            description: `Complaint ${c.id}: ${c.subject} — received ${c.receivedDate}`,
            priority: TaskPriority.HIGH,
            dueDate: new Date(new Date(c.receivedDate).getTime() + EIGHT_WEEKS_MS).toISOString(),
            sourceEventId: `deadline_${c.id}`,
          });
          tasks.push(task);
        }
      }
    }
    return tasks;
  }

  _transitionComplaint(complaint, newStatus) {
    const allowed = COMPLAINT_TRANSITIONS[complaint.status];
    if (!allowed || !allowed.includes(newStatus)) {
      throw new Error(`Cannot transition complaint from ${complaint.status} to ${newStatus}`);
    }
    complaint.status = newStatus;
  }

  // ============================================================
  // CUSTOMER 360 VIEW
  // ============================================================

  getCustomer360(customerId) {
    // Customer details from KYC
    const customer = this.kyc?.getCustomer(customerId) || null;
    const kycCase = this.kyc?.getKYCCase(customerId) || null;

    // Accounts and balances from orchestrator's ledger (if available)
    let accounts = [];
    let recentTransactions = [];
    const ledger = this.orchestrator?.ledger || null;
    if (ledger) {
      const rawAccounts = ledger.getAllAccounts({ customerId });
      accounts = rawAccounts.map(acc => {
        try {
          const bal = ledger.getBalance(acc.id);
          return { ...acc, balance: bal?.available ?? bal?.posted ?? 0, balanceDetail: bal };
        } catch (_) {
          return { ...acc, balance: 0 };
        }
      });
      // Get recent transactions across all customer accounts
      const allTxns = [];
      for (const acc of rawAccounts) {
        const txns = ledger.getTransactionsForAccount(acc.id, {});
        for (const txn of txns) allTxns.push(txn);
      }
      // Deduplicate by transaction ID and take last 20
      const seen = new Set();
      const uniqueTxns = [];
      for (const txn of allTxns) {
        if (!seen.has(txn.id)) {
          seen.add(txn.id);
          uniqueTxns.push(txn);
        }
      }
      recentTransactions = uniqueTxns
        .sort((a, b) => new Date(b.createdAt || b.timestamp || 0) - new Date(a.createdAt || a.timestamp || 0))
        .slice(0, 20);
    }

    // Active facilities from lending
    const facilities = this.lending
      ? this.lending.getAllFacilities().filter(f => f.customerId === customerId)
      : [];

    // Monitoring alerts
    const allAlerts = this.monitoring?.getAlerts(customerId) || [];
    const openAlerts = allAlerts.filter(a => a.status === 'OPEN' || a.status === 'UNDER_REVIEW');
    const recentClosedAlerts = allAlerts
      .filter(a => a.status !== 'OPEN' && a.status !== 'UNDER_REVIEW')
      .slice(-5);

    // Open SARs from compliance
    const sars = this.compliance?.getSARsByCustomer(customerId) || [];
    const openSARs = sars.filter(s => s.status !== 'ACKNOWLEDGED');

    // Contact history (last 10)
    const contactHistory = this.getContactHistory(customerId).slice(0, 10);

    // Open tasks
    const openTasks = this.getTasksByCustomer(customerId)
      .filter(t => t.status === TaskStatus.OPEN);

    return {
      customer,
      kycStatus: kycCase?.status || null,
      riskRating: kycCase?.riskRating || null,
      nextReviewDate: kycCase?.nextReviewDate || null,
      accounts,
      recentTransactions,
      facilities,
      openAlerts,
      recentClosedAlerts,
      openSARs,
      contactHistory,
      openTasks,
    };
  }

  // ============================================================
  // RM PORTFOLIO SUMMARY
  // ============================================================

  getRMPortfolio(rmId) {
    const allCases = this.kyc?.getAllCases() || [];

    const rmCustomers = [];
    for (const kycCase of allCases) {
      const assignedRM = kycCase.rmAssessment?.rmId || null;
      if (assignedRM !== rmId) continue;

      const customer = this.kyc.getCustomer(kycCase.customerId);
      if (!customer) continue;

      // Total balances
      let totalBalance = 0;
      const ledger = this.orchestrator?.ledger || null;
      if (ledger) {
        const accs = ledger.getAllAccounts({ customerId: customer.id });
        for (const acc of accs) {
          const bal = ledger.getBalance(acc.id);
          totalBalance += bal?.available || 0;
        }
      }

      // Open alert count
      const alerts = this.monitoring?.getAlerts(customer.id) || [];
      const openAlertCount = alerts.filter(a => a.status === 'OPEN' || a.status === 'UNDER_REVIEW').length;

      // Open task count
      const tasks = this.getTasksByCustomer(customer.id).filter(t => t.status === TaskStatus.OPEN);

      // Days since last contact
      const contacts = this.getContactHistory(customer.id);
      let daysSinceLastContact = null;
      if (contacts.length > 0) {
        const lastDate = new Date(contacts[0].date);
        daysSinceLastContact = Math.floor((Date.now() - lastDate.getTime()) / 86400000);
      }

      rmCustomers.push({
        customerId: customer.id,
        legalName: customer.legalName,
        riskRating: kycCase.riskRating,
        kycStatus: kycCase.status,
        totalBalance,
        openAlertCount,
        openTaskCount: tasks.length,
        daysSinceLastContact,
      });
    }

    return rmCustomers;
  }

  // ============================================================
  // PIPELINE TRACKING
  // ============================================================

  getOnboardingPipeline() {
    const cases = this.kyc?.getAllCases() || [];
    const pipeline = {
      DATA_COLLECTION: [],
      SCREENING: [],
      RM_REVIEW: [],
      APPROVED: [],
      REJECTED: [],
      REVIEW_DUE: [],
      UNDER_REVIEW: [],
      CLOSED: [],
    };

    for (const kycCase of cases) {
      const customer = this.kyc.getCustomer(kycCase.customerId);
      const entry = {
        customerId: kycCase.customerId,
        legalName: customer?.legalName || 'Unknown',
        riskRating: kycCase.riskRating,
        updatedAt: kycCase.updatedAt,
      };
      if (pipeline[kycCase.status]) {
        pipeline[kycCase.status].push(entry);
      }
    }

    return pipeline;
  }

  getLendingPipeline() {
    const facilities = this.lending?.getAllFacilities() || [];
    const pipeline = {
      DRAFT: [],
      APPROVED: [],
      ACTIVE: [],
      EXPIRED: [],
      DEFAULTED: [],
      CLOSED: [],
    };

    for (const fac of facilities) {
      const entry = {
        facilityId: fac.id,
        customerId: fac.customerId,
        customerName: fac.customerName,
        type: fac.type,
        committedAmount: fac.committedAmount,
        currency: fac.currency,
        status: fac.status,
      };
      if (pipeline[fac.status]) {
        pipeline[fac.status].push(entry);
      }
    }

    return pipeline;
  }
}

export { ContactType, TaskPriority, TaskStatus, ComplaintChannel, ComplaintStatus, COMPLAINT_TRANSITIONS, CRMEngine };
